import React, { useMemo, useState } from 'react';

import styled, { css, useTheme } from 'styled-components';
import { rankingOrderMapper } from 'type/dashboard';
import { ICreatorRankDB } from 'type/db';

import {
	useGetCreatorRankings,
	useGetDashboardGroupSummary,
	useGetMissionRainkings,
} from 'hooks/queries/dashboard';
import useCreatorRankTable from 'hooks/table/useCreatorRankTable';
import useGetSearchParams from 'hooks/useGetSearchParams';
import CoreRadioChips from 'shared/chips/CoreRadioChips';
import CoreLoading from 'shared/CoreLoading';
import CoreSelect from 'shared/CoreSelect';
import style from 'styles/styled-components/style';
import { rankingGroupOptionList } from 'utils/date';
import { creator_ranking_header_arr, search_header_arr } from 'utils/table';

function CreatorRanking() {
	const [groupId] = useGetSearchParams(['groupId']);

	const [hover, setHover] = useState<number>();
	const [orderBy, setOrderBy] = useState<string>('');

	const [selectedCretorRankingOrderBy, setSelectedCretorRankingOrderBy] =
		useState<string>(rankingOrderMapper[0].label);
	const [selectedMission, setSelectedMission] = useState('전체 미션');
	const match_cell_mapper = useCreatorRankTable();

	const { data: groupSummary } = useGetDashboardGroupSummary(groupId);

	const missionList = useMemo(() => {
		const allMissions = [{ label: '전체 미션', value: 'all' }];

		groupSummary?.missionStatus?.forEach((mission, index) => {
			allMissions.push({
				label: `${index + 1}차 미션`,
				value: mission.missionNumber.toString(),
			});
		});

		return allMissions;
	}, [groupSummary]);

	const missionValue = missionList?.find((item) => item.label === selectedMission)?.value;

	const selectedRainkingOrderBy = rankingOrderMapper.find(
		(item) => item.label === selectedCretorRankingOrderBy,
	);

	const {
		data: creatorAllRankings,
		isLoading: creatorAllRankingsLoading,
		isError,
	} = useGetCreatorRankings(
		groupId,
		selectedRainkingOrderBy?.value,
		selectedMission === '전체 미션',
	);

	const { data: missionRainkings, isLoading: missionRainkingsLoading } =
		useGetMissionRainkings(
			groupId,
			missionValue ?? '',
			selectedRainkingOrderBy?.value,
			selectedMission !== '전체 미션',
		);

	let creatorRainkings: ICreatorRankDB[] = [];

	// 메인 로직
	const rankings =
		selectedMission === '전체 미션' ? creatorAllRankings : missionRainkings;

	creatorRainkings = [...(rankings || [])];

	if (creatorAllRankingsLoading || missionRainkingsLoading) {
		return (
			<>
				<Grid flex="between" _margin="0 0 24px">
					<CoreRadioChips
						group_type="carousel"
						style_type="default"
						_width="max-content"
						chips_list={rankingOrderMapper.map((item) => item.label)}
						selected_chips={selectedCretorRankingOrderBy}
						setSelectedChips={setSelectedCretorRankingOrderBy}
					/>

					<CoreSelect
						_width="170px"
						_height="32px"
						select_type={'box'}
						option_list={[...rankingGroupOptionList]}
						selected_option={selectedMission}
						setSelectedOption={(e: string) => setSelectedMission(e)}
					/>
				</Grid>

				<TableHeaderWrapper>
					{creator_ranking_header_arr.map((item, idx) => (
						<React.Fragment key={idx}>
							<Grid
								_width={`${item.width}px`}
								flex={item.align ?? 'center'}
								align="center"
								// onClick={() => {
								// 	if (item.name === 'contents' || item.name === 'link') return;
								// 	if (orderBy === item.name) {
								// 		setOrderBy('');
								// 		return;
								// 	}
								// 	setOrderBy(item.name);
								// }}
								// onHoverStart={() => {
								// 	setHover(idx);
								// }}
								// onHoverEnd={() => {
								// 	setHover(undefined);
								// }}
								_padding="0 10px"
							>
								<Text white_space="pre" typo="span">
									{item.name === 'rankingPoint' && selectedCretorRankingOrderBy}
									{item.name !== 'rankingPoint' && item.header}
								</Text>
							</Grid>

							{/* 구분 선 */}
							{idx !== creator_ranking_header_arr?.length && idx !== 0 && (
								<Contour _height="40%" _width="1px" />
							)}
						</React.Fragment>
					))}
				</TableHeaderWrapper>

				{/* loading  */}
				{[...Array(15).fill(0)].map((data, row_idx) => (
					<React.Fragment key={row_idx}>
						<TableRow selected={false}>
							<Grid _width="100%" flex="center">
								<CoreLoading color_type="gray" />
							</Grid>
						</TableRow>
					</React.Fragment>
				))}
			</>
		);
	}

	return (
		<>
			<Grid flex="between" _margin="0 0 24px">
				<CoreRadioChips
					group_type="carousel"
					style_type="default"
					_width="max-content"
					chips_list={rankingOrderMapper.map((item) => item.label)}
					selected_chips={selectedCretorRankingOrderBy}
					setSelectedChips={setSelectedCretorRankingOrderBy}
				/>

				<CoreSelect
					_width="170px"
					_height="32px"
					select_type={'box'}
					option_list={missionList.map((mission) => mission.label)}
					selected_option={selectedMission}
					setSelectedOption={(e: string) => setSelectedMission(e)}
				/>
			</Grid>

			<TableHeaderWrapper>
				{creator_ranking_header_arr.map((item, idx) => (
					<React.Fragment key={idx}>
						<Grid
							_width={`${item.width}px`}
							flex={item.align ?? 'center'}
							align="center"
							_padding="0 10px"
						>
							<Text white_space="pre" typo="span">
								{item.name === 'rankingPoint' && selectedCretorRankingOrderBy}
								{item.name !== 'rankingPoint' && item.header}
							</Text>
						</Grid>

						{/* 구분 선 */}
						{idx !== creator_ranking_header_arr?.length && idx !== 0 && (
							<Contour _height="40%" _width="1px" />
						)}
					</React.Fragment>
				))}
			</TableHeaderWrapper>

			{creatorRainkings.map((data, row_idx) => (
				<React.Fragment key={row_idx}>
					<TableRow selected={false}>
						{creator_ranking_header_arr.map((item, idx) => (
							<React.Fragment key={idx}>
								{match_cell_mapper[item.name](data, idx, row_idx)}
							</React.Fragment>
						))}
					</TableRow>
				</React.Fragment>
			))}
		</>
	);
}

const { Grid, Text } = style;

const TableHeaderWrapper = styled(Grid)`
	height: 66px;
	background-color: ${({ theme }) => theme.colors.gray.gray_white};
	border-bottom: 1px solid ${({ theme }) => theme.colors.gray.gray15};
	border-top-left-radius: 8px;
	border-top-right-radius: 8px;
	padding: 0 16px;

	${({ theme }) => theme.common.flexStart};

	div {
		cursor: ${(props) => (props.onClick ? 'pointer' : 'default')};
	}
`;

const TableRow = styled(Grid)<{ selected: boolean }>`
	min-width: calc(100% - 32px);
	height: 65px;

	border-radius: 8px;
	margin-top: 8px;
	padding: 0 16px;
	/* cursor: pointer; */

	${({ theme }) => theme.common.flexStart}

	background-color: ${({ theme }) => theme.colors.gray.gray_white};
	border: 1px solid ${({ theme }) => theme.colors.gray.gray_white};

	${({ theme, selected }) =>
		selected &&
		css`
			border: 1px solid ${theme.colors.uplus.primary10_lighten};
		`}

	&:hover {
		cursor: ${(props) => (props.onClick ? 'pointer' : 'default')};
		border-color: ${({ theme }) => theme.colors.uplus.bg10_lighten};
		background-color: ${({ theme }) => theme.colors.uplus.bg10_lighten};
		${({ theme, selected }) =>
			selected &&
			css`
				border-color: ${theme.colors.uplus.primary50_key};
			`}
	}
`;

const Contour = styled(Grid)`
	background-color: ${({ theme }) => theme.colors.gray.gray15};
`;

export default CreatorRanking;
